/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Edit, Trash2, Loader2, MoreVertical } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import FormDrawer from '../components/FormDrawer';
import { useRoles, usePermissions } from '../services/queries';
import {
  useCreateRole,
  useUpdateRole,
  useSyncPermissions,
  useDeleteRole,
} from '../services/mutations';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

const initialFormState = {
  roleName: '',
  permissions: [],
};

const RoleManagement = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { isLoading, data, error, refetch } = useRoles();
  const { data: permissionsData } = usePermissions();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const syncPermissions = useSyncPermissions();
  const deleteRole = useDeleteRole();
  const nav = useNavigate();
  const { checkPermissionElement } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [editMode, setEditMode] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [formError, setFormError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Pagination handlers
  const handlePageChange = (value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      if (editMode) {
        await updateRole.mutateAsync({ code: selectedCode, body: form });
      } else {
        await createRole.mutateAsync(form);
      }
      setIsOpen(false);
      setForm(initialFormState);
      refetch();
    } catch (error) {
      setFormError(error.message);
    }
  };

  const handleDelete = useCallback(
    async (code) => {
      try {
        await deleteRole.mutateAsync(code);
        refetch();
      } catch (error) {
        console.error('Error during role delete:', error);
      }
    },
    [deleteRole, refetch],
  );

  const handleEdit = useCallback((role) => {
    setForm(role);
    setSelectedCode(role.roleName);
    setEditMode(true);
    setIsOpen(true);
  }, []);

  const handleSyncPermissions = useCallback(
    async (code, permissions) => {
      try {
        await syncPermissions.mutateAsync({ code, permissions });
        refetch();
      } catch (error) {
        console.error('Error during permissions sync:', error);
      }
    },
    [syncPermissions, refetch],
  );

  const renderMore = useCallback((data) => {
    if (data.length === 0) return '-';
    if (data.length > 5) {
      const slicedData = data.slice(0, 5);
      const moreCount = data.length - 5;
      return (
        <div className="flex flex-wrap gap-1">
          {slicedData.map((permission) => (
            <Badge key={permission} variant="secondary" className="text-xs">
              {permission}
            </Badge>
          ))}
          <Badge variant="secondary" className="text-xs">
            ...and {moreCount} more
          </Badge>
        </div>
      );
    } else {
      return (
        <div className="flex flex-wrap gap-1">
          {data.map((permission) => (
            <Badge key={permission} variant="secondary" className="text-xs">
              {permission}
            </Badge>
          ))}
        </div>
      );
    }
  }, []);

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (!searchQuery.trim()) return data.data;

    const query = searchQuery.toLowerCase();
    return data.data.filter(
      (role) =>
        role.name.toLowerCase().includes(query) ||
        role.code.toLowerCase().includes(query) ||
        role.applications.some((app) => app.name.toLowerCase().includes(query)),
    );
  }, [data?.data, searchQuery]);

  // Get paginated data and total pages
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, page, rowsPerPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredData.length / rowsPerPage);
  }, [filteredData, rowsPerPage]);

  const renderTableRows = useMemo(
    () =>
      paginatedData?.length > 0 ? (
        paginatedData.map((role, i) => (
          <TableRow key={role.name} className="border-b-2 border-gray-200">
            <TableCell width="70" className="border-b-2 border-gray-200">
              {(page - 1) * rowsPerPage + i + 1}
            </TableCell>
            <TableCell className="border-b-2 border-gray-200">
              {role.name}
            </TableCell>
            <TableCell className="border-b-2 border-gray-200">
              <Badge variant="secondary">{role.code}</Badge>
            </TableCell>
            <TableCell className="border-b-2 border-gray-200">
              <div className="flex flex-wrap gap-1">
                {role.applications.map((app) => (
                  <Badge key={app.code} variant="secondary" className="text-xs">
                    {app.name}
                  </Badge>
                ))}
              </div>
            </TableCell>
            <TableCell className="border-b-2 border-gray-200">
              {renderMore(role?.permissions)}
            </TableCell>
            <TableCell className="sticky-action w-[60px]">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 sm:h-9 sm:w-9"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-40 p-1" align="end">
                  <div className="flex flex-col gap-0.5">
                    {checkPermissionElement(
                      'superapp:role-management:button:edit',
                    ) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => nav(`edit/${role.code}`)}
                        className="justify-start h-8 px-2"
                      >
                        <Edit className="size-4 mr-2" />
                        Edit
                      </Button>
                    )}
                    {checkPermissionElement(
                      'superapp:role-management:button:delete',
                    ) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(role.code)}
                        className="justify-start h-8 px-2 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="size-4 mr-2" />
                        Delete
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={6} className="text-center">
            No roles found
          </TableCell>
        </TableRow>
      ),
    [
      paginatedData,
      checkPermissionElement,
      nav,
      page,
      rowsPerPage,
      handleDelete,
      renderMore,
    ],
  );

  // Generate pagination items
  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
        </PaginationItem>,
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start">
            <span className="flex h-9 w-9 items-center justify-center">
              ...
            </span>
          </PaginationItem>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => handlePageChange(i)}
            isActive={i === page}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end">
            <span className="flex h-9 w-9 items-center justify-center">
              ...
            </span>
          </PaginationItem>,
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    return items;
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold mb-2">Role Management</h1>
          <p className="muted-text">Kelola role, aplikasi, dan permissions</p>
        </div>
        {checkPermissionElement('superapp:role-management:button:create') && (
          <Button
            onClick={() => nav('add')}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Role
          </Button>
        )}
      </div>

      <div className="section-card p-6">
        <div className="flex gap-4 mb-4 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="search" className="mb-2 block">
              Search
            </Label>
            <Input
              id="search"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search by role name, code, or application..."
              className="w-full"
            />
          </div>
          <div>
            <Button
              variant="outline"
              onClick={handleClearSearch}
              className="h-10"
            >
              Clear
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        {isLoading ? (
          <div className="flex justify-center items-center mt-20">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            <div className="table-card">
              <div className="table-wrapper">
                <Table className="border-2 border-gray-200">
                  <TableHeader>
                    <TableRow className="border-b-2 border-gray-200">
                      <TableHead className="w-[50px] font-bold border-b-2 border-gray-200">
                        No
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Role
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Code
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Applications
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Permissions
                      </TableHead>
                      <TableHead className="sticky-action w-[60px] font-bold border-b-2 border-gray-200">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>{renderTableRows}</TableBody>
                </Table>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Jumlah per halaman:
                </span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={(value) =>
                    handleRowsPerPageChange(Number(value))
                  }
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => handlePageChange(Math.max(1, page - 1))}
                        className={
                          page === 1
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>
                    {getPaginationItems()}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          handlePageChange(Math.min(totalPages, page + 1))
                        }
                        className={
                          page === totalPages
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </>
        )}
      </div>

      <FormDrawer isOpen={isOpen} setIsOpen={setIsOpen}>
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[500px] flex flex-col h-full"
        >
          <div>
            <h2 className="text-xl font-semibold">
              {editMode ? 'Edit Role' : 'Add Role'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Configure role details and permissions.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto space-y-6">
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="roleName">Role Name</Label>
              <Input
                id="roleName"
                name="roleName"
                value={form.roleName}
                onChange={handleChangeInput}
                placeholder="e.g. Content Editor"
                className="w-full"
              />
            </div>

            <div className="space-y-3">
              <Label>Permissions</Label>
              <div className="border rounded-lg bg-card">
                <div className="p-3 border-b bg-muted/30">
                  <Input
                    placeholder="Filter permissions..."
                    className="h-8 text-sm"
                    // Add local filter state logic here if needed, simple implementation below
                    onChange={(e) => {
                      // This would need a local state for filtering the permissions list display
                      // For now we just keep the input as requested in plan
                    }}
                  />
                </div>
                <div className="p-4 max-h-[300px] overflow-y-auto space-y-3">
                  {permissionsData?.data?.length > 0 ? (
                    permissionsData.data.map((permission) => (
                      <div
                        key={permission}
                        className="flex items-start space-x-3 p-2 hover:bg-accent rounded-md transition-colors"
                      >
                        <Checkbox
                          id={`permission-${permission}`}
                          checked={form.permissions.includes(permission)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setForm((prev) => ({
                                ...prev,
                                permissions: [...prev.permissions, permission],
                              }));
                            } else {
                              setForm((prev) => ({
                                ...prev,
                                permissions: prev.permissions.filter(
                                  (p) => p !== permission,
                                ),
                              }));
                            }
                          }}
                          className="mt-0.5"
                        />
                        <div className="grid gap-1.5 leading-none">
                          <Label
                            htmlFor={`permission-${permission}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {permission}
                          </Label>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No permissions available
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Select the permissions to assign to this role.
              </p>
            </div>
          </div>

          <div className="pt-3 border-t bg-muted/10 flex justify-end gap-3">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createRole.isPending || updateRole.isPending}
            >
              {(createRole.isPending || updateRole.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editMode ? 'Save Changes' : 'Create Role'}
            </Button>
          </div>
        </form>
      </FormDrawer>
    </div>
  );
};

export default RoleManagement;
